/**
 * Projekt am Beleg wählen — durchsuchbar statt langer Dropdown-Liste.
 *
 * ScanFlow liefert seit 19.10.2026 auch abgeschlossene Projekte (rund 180),
 * weil Belege oft erst nach dem Ende der Baustelle eintreffen. Als <select>
 * war das auf dem Telefon nicht mehr zu bedienen, und ein Beleg an einem
 * abgeschlossenen Projekt erschien vorher als „kein Projekt".
 */
import { useState } from "react";
import { istAbgeschlossen, type ProjektShort } from "../../lib/api";
import "./ProjektWahl.css";

const MAX_TREFFER = 30;

export default function ProjektWahl({
  projekte,
  value,
  onChange,
  loading,
}: {
  projekte: ProjektShort[];
  value: string;
  onChange: (id: string) => void;
  loading?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [offen, setOffen] = useState(false);
  const gewaehlt = value ? projekte.find((p) => p.id === value) : undefined;
  const term = query.trim().toLowerCase();
  const treffer = (term
    ? projekte.filter((p) => `${p.title} ${p.kunde_name} ${p.ort}`.toLowerCase().includes(term))
    : projekte
  ).slice(0, MAX_TREFFER);

  if (value && !offen) {
    return (
      <div className="sf-pw-gewaehlt">
        <div className="sf-pw-tt">
          <span className="sf-pw-name">
            {gewaehlt ? gewaehlt.title || "(ohne Titel)" : loading ? "Lade Projekt…" : "Projekt nicht gefunden"}
          </span>
          {gewaehlt && (gewaehlt.kunde_name || istAbgeschlossen(gewaehlt)) && (
            <span className="sf-pw-sub">
              {gewaehlt.kunde_name}
              {istAbgeschlossen(gewaehlt) && <em className="sf-pw-tag">abgeschlossen</em>}
            </span>
          )}
        </div>
        <button type="button" className="sf-pw-btn" onClick={() => { setQuery(""); setOffen(true); }}>
          Ändern
        </button>
      </div>
    );
  }

  return (
    <div className="sf-pw">
      <input
        className="sf-pw-input"
        value={query}
        autoFocus={offen}
        onChange={(e) => { setQuery(e.target.value); setOffen(true); }}
        onFocus={() => setOffen(true)}
        onBlur={() => setTimeout(() => setOffen(false), 150)}
        placeholder="Projekt, Kunde oder Ort suchen…"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
      />
      {offen && (
        <div className="sf-pw-liste" role="listbox">
          {value && (
            <button type="button" className="sf-pw-opt" onMouseDown={() => { onChange(""); setOffen(false); }}>
              <span className="sf-pw-name">— kein Projekt —</span>
            </button>
          )}
          {loading && <div className="sf-pw-leer">Lade Projekte…</div>}
          {!loading && treffer.length === 0 && (
            <div className="sf-pw-leer">Kein Projekt gefunden. Tipp: nach Kunde oder Ort suchen.</div>
          )}
          {treffer.map((p) => (
            <button
              type="button"
              key={p.id}
              role="option"
              aria-selected={p.id === value}
              className={"sf-pw-opt" + (p.id === value ? " is-sel" : "")}
              onMouseDown={() => { onChange(p.id); setQuery(""); setOffen(false); }}
            >
              <span className="sf-pw-name">{p.title || "(ohne Titel)"}</span>
              {(p.kunde_name || istAbgeschlossen(p)) && (
                <span className="sf-pw-sub">
                  {p.kunde_name}
                  {istAbgeschlossen(p) && <em className="sf-pw-tag">abgeschlossen</em>}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
